import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button/Button';
import styles from './CommitReview.module.css';

// The foot of a commit review, single or list form: why the commit is (or is
// not yet) possible, then Cancel and the commit itself. The reason turns green
// once nothing stands in the way. After a failed attempt the failure takes the
// reason's place and stays until the next attempt — the toast that also says
// so can be dismissed, and the review must not then look as if nothing happened.

interface IReviewFooterProps {
  /** One line: what still blocks the commit, or that it is ready. */
  reason: string;
  /** The last attempt's failure, shown in place of the reason. */
  error?: string | null;
  canCommit: boolean;
  /** A commit is in flight — Cancel waits for it. */
  pending: boolean;
  confirmLabel: string;
  confirmTestId: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ReviewFooter({ reason, error, canCommit, pending, confirmLabel, confirmTestId, onCancel, onConfirm }: IReviewFooterProps) {
  const { t } = useTranslation();
  return (
    <footer className={styles.footer}>
      {error ? (
        <span className={`${styles.hint} ${styles.failed}`} role="alert" data-testid="commit-error">{error}</span>
      ) : (
        <span className={`${styles.hint} ${canCommit ? styles.good : ''}`}>{reason}</span>
      )}
      <span className={styles.grow} />
      <Button variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
        {t('common.cancel')}
      </Button>
      <Button variant="accent" size="sm" onClick={onConfirm} disabled={!canCommit} aria-busy={pending} data-testid={confirmTestId}>
        {confirmLabel}
      </Button>
    </footer>
  );
}
